import axios from 'axios'

/* @ngInject */
function uploadService (Utils, Upload, NewAssetService, MultipartUploadService, AssetThumbnailGeneratorService, UploadQueueService, CONSTANTS, MimeService) {
  const TEN_MB_IN_BYTES = 10 * 1024 * 1024
  const uploadQueue = UploadQueueService.createQueue()

  var service = {
    upload: upload,
    uploadWithCustomProgress: uploadWithCustomProgress,
    basicUpload,
    uploadToS3,
    uploadMultipleFileToS3,
    generateThumbnailInBrowserAndUploadToS3,
    generateThumbnailInBrowserAndUploadMultipleFiles
  }
  return service

  /**
   * @description Uploads an array of files to S3 and Generates thumbnail synchronously in User's browser. If something fails, it will use a fallback to upload it using uploadToS3 and generate thumbnail async in Lambda
   * @param {File[]} files
   * @param {Object} options
   * @param {Boolean} options.generateThumbnailSync If true, we will generate thumbnails synchronously in the API. Otherwise, we will generate it in Lambda.
   * @param {Boolean} options.skipAssetCreation If true, we will not create the file in the database. We will only upload it to S3 and return the metadata
   * @param {String} options.existentS3Key This is used to upload a file and overwrite an specific file in S3 (Used for assets versioning)
   * @param {Function} options.onUploadProgress This is a function that will be called everytime the upload progress changes
   * @param {Object} options.asset Send asset's metadata to database
   * @param {Boolean} options.asset.isOrganized Control whether the file will be created as Organized or not
   * @param {Number} options.asset.folderId Set the initial folder of the asset on creation
   * @returns {Promise<Asset[]>} Returns Asset with AssetFiles Object
   */
  function generateThumbnailInBrowserAndUploadMultipleFiles (files, options = {}) {
    if (!files?.length) {
      return Promise.resolve()
    }

    const uploadedFiles = files.map(file => generateThumbnailInBrowserAndUploadToS3(file, options))

    return Promise.allSettled(uploadedFiles)
      .then((promisesResult) => {
        if (!promisesResult.length) {
          return []
        }

        const successFullPromises = promisesResult.filter((promise) => promise.status === 'fulfilled')
        return successFullPromises.map((uploadedFiles) => {
          return {
            ...uploadedFiles.value,
            src: NewAssetService.getAssetThumbnailUrl(uploadedFiles.value.id)
          }
        })
      })
  }

  /**
   * @description Uploads a single file to S3 and Generates thumbnail synchronously in User's browser. If something fails, it will use a fallback to upload it using uploadToS3 and generate thumbnail async in Lambda
   * @param {File} file
   * @param {Object} options
   * @param {Boolean} options.generateThumbnailSync If true, we will generate thumbnails synchronously in the API. Otherwise, we will generate it in Lambda.
   * @param {Boolean} options.skipAssetCreation If true, we will not create the file in the database. We will only upload it to S3 and return the metadata
   * @param {String} options.existentS3Key This is used to upload a file and overwrite an specific file in S3 (Used for assets versioning)
   * @param {Function} options.onUploadProgress This is a function that will be called everytime the upload progress changes
   * @param {Object} options.asset Send asset's metadata to database
   * @param {Boolean} options.asset.isOrganized Control wether the file will be created as Organized or not
   * @param {Number} options.asset.folderId Set the initial folder of the asset on creation
   * @returns {Promise<Asset>} Returns Asset with AssetFiles Object
   */
  function generateThumbnailInBrowserAndUploadToS3 (file, options = {}) {
    if (!file?.size) return
    if (file.size > TEN_MB_IN_BYTES) return uploadToS3(file, { ...options })

    const isImage = file.type.includes('image')

    if (!isImage) {
      return uploadToS3(file, { ...options })
    }

    // generateThumbnailSync and skipAssetCreation is being extracted from `options` here because we want to save their original value in order to use it in the fallback function
    const { qualities = ['LOW', 'MID', 'HIGH'], webp = true, png = true, generateThumbnailSync, skipAssetCreation } = options

    // we will generate thumb async if this synchronous process fails
    const uploadFallback = () => uploadToS3(file, {
      ...options,
      skipAssetCreation,
      generateThumbnailSync
    })

    // sanitize options object
    delete options.qualities
    delete options.png
    delete options.webp

    // This is important in order to avoid uploadToS3 method creates our file in the database. We will call another endpoint to create the file and also attach the thumbnails.
    options.skipAssetCreation = true
    // This is required in order to avoid S3 from sending a notification to SQS to generate the thumbnails for this file. We will generate the thumbnail locally in user's browser.
    options.generateThumbnailSync = true

    try {
      return AssetThumbnailGeneratorService.generateThumbnailFromImage(file, { png, webp, qualities })
        .then((thumbnails) => {
          return uploadToS3(file, options)
            .then((createdFile) => {
              const s3Key = createdFile.s3Key

              const uploadPromises = thumbnails.map((thumb) => {
                // these props `quality` and `format` are injected in the *.wasm.js file (imagemagick.wasm.js or ffmpeg.wasm.js)
                const quality = thumb.quality.toUpperCase()
                const format = thumb.format.toLowerCase()
                const existentS3Key = `${s3Key}-thumbnail-${format}-${quality}`

                return uploadToS3(thumb.file, { ...options, existentS3Key })
              })

              return Promise.all(uploadPromises).then((uploadedThumbs) => {
                const extension = file.name.split('.').pop()
                const isMpegFile = Utils.isMpegFile(extension)
                const fileTypePromise = isMpegFile ? Utils.getFileTypeForMpegFiles(file) : Promise.resolve(MimeService.getType(file.name) || file.type)

                return fileTypePromise.then((fileType) => {
                  return NewAssetService.createAsset({
                    s3Key: file.s3Key,
                    name: file.name,
                    size: file.size,
                    contentType: fileType,
                    isOrganized: Boolean(options?.asset?.isOrganized),
                    folderId: options?.asset?.folderId,
                    thumbnails: uploadedThumbs
                  }, { generateThumbnailInBrowser: true })
                })
              })
            })
        }).catch((err) => {
          console.error('generate-thumbnail-sync-error', err)
          return uploadFallback()
        })
    } catch (err) {
      console.error('generate-thumbnail-sync-error', err)
      return uploadFallback()
    }
  }

  /**
   * @description Uploads a single file to S3
   * @param {File} file
   * @param {Object} options
   * @param {Boolean} options.generateThumbnailSync If true, we will generate thumbnails synchronously in the API. Otherwise, we will generate it in Lambda.
   * @param {Boolean} options.skipAssetCreation If true, we will not create the file in the database. We will only upload it to S3 and return the metadata
   * @param {String} options.existentS3Key This is used to upload a file and overwrite an specific file in S3 (Used for assets versioning)
   * @param {Function} options.onUploadProgress This is a function that will be called everytime the upload progress changes
   * @param {Object} options.asset Send asset's metadata to database
   * @param {Boolean} options.asset.isOrganized Control whether the file will be created as Organized or not
   * @param {Number} options.asset.folderId Set the initial folder of the asset on creation
   * @returns {Promise<Asset>} Returns Asset with AssetFiles Object
   */
  function uploadToS3 (file, options = {}) {
    if (!file) {
      return Promise.resolve()
    }

    // Usar upload multipart para arquivos grandes
    if (file.size > TEN_MB_IN_BYTES) {
      return MultipartUploadService.uploadFile(file, options)
    }

    return new Promise((resolve, reject) => {
      // Adicionar o arquivo à fila de upload
      uploadQueue.add({
        file,
        options,
        resolve,
        reject
      })

      // Processar a fila com concorrência máxima de 3
      uploadQueue.process({
        maxConcurrency: CONSTANTS.FILE_UPLOAD_MAX_CONCURRENCY,
        handler: processFileUpload,
        callback: (response, item) => {
          item.resolve(response)
        }
      })
    })
  }

  /**
   * @description Processa o upload de um único arquivo para S3
   * @param {Object} item Item da fila contendo arquivo e opções
   * @returns {Promise<Asset>}
   */
  function processFileUpload (item) {
    const { file, options } = item
    const s3KeySuffix = options?.generateThumbnailSync ? '-sync' : ''
    const extension = file.name.split('.').pop()

    return NewAssetService.getPresignedUploadUrl(s3KeySuffix, extension, file.size, options?.existentS3Key)
      .then((presignedUploadInfo) => {
        return prepareAndUploadFile(file, presignedUploadInfo, extension, options)
      })
  }

  /**
   * @description Prepara e envia o arquivo para S3
   * @param {File} file Arquivo a ser enviado
   * @param {Object} presignedUploadInfo Informações de upload pré-assinadas
   * @param {String} extension Extensão do arquivo
   * @param {Object} options Opções de upload
   * @returns {Promise<Asset>}
   */
  function prepareAndUploadFile (file, presignedUploadInfo, extension, options) {
    const { url, fields } = presignedUploadInfo
    const payload = new FormData()
    const isMpegFile = Utils.isMpegFile(extension)

    return getFileContentType(file, extension, isMpegFile)
      .then((fileType) => {
        // Preparar o payload
        for (const field in fields) {
          payload.append(field, fields[field])
        }

        payload.append('x-amz-meta-extension', extension)
        payload.append('Content-Type', fileType)
        payload.append('file', file)

        const abortFileUpload = new AbortController()

        return axios.post(url, payload, {
          signal: abortFileUpload.signal,
          onUploadProgress: (event) => handleUploadProgress(event, file, abortFileUpload, options)
        })
          .then(() => {
            file.s3Key = fields.key
            return handleUploadSuccess(file, fileType, options)
          })
      })
  }

  /**
   * @description Obtém o tipo de conteúdo do arquivo
   * @param {File} file Arquivo
   * @param {String} extension Extensão do arquivo
   * @param {Boolean} isMpegFile Se é um arquivo MPEG
   * @returns {Promise<String>} Tipo de conteúdo
   */
  function getFileContentType (file, extension, isMpegFile) {
    return isMpegFile
      ? Utils.getFileTypeForMpegFiles(file)
      : Promise.resolve(MimeService.getType(file.name) || file.type)
  }

  /**
   * @description Manipula o progresso do upload
   * @param {ProgressEvent} event Evento de progresso
   * @param {File} file Arquivo sendo enviado
   * @param {AbortController} abortController Controlador para abortar o upload
   * @param {Object} options Opções de upload
   */
  function handleUploadProgress (event, file, abortController, options) {
    const progress = Math.round((event.loaded * 100) / event.total)
    file.abortUpload = abortController.abort.bind(abortController)

    if (typeof options.onUploadProgress === 'function') {
      options.onUploadProgress(file, progress)
    }
  }

  /**
   * @description Manipula o sucesso do upload
   * @param {File} file Arquivo enviado
   * @param {String} fileType Tipo de conteúdo do arquivo
   * @param {Object} options Opções de upload
   * @returns {Promise<Asset>} Asset criado ou metadados do arquivo
   */
  function handleUploadSuccess (file, fileType, options) {
    if (options?.skipAssetCreation) {
      return {
        s3Key: file.s3Key,
        name: file.name,
        size: file.size,
        contentType: fileType,
        src: NewAssetService.getAssetThumbnailUrl(file.id),
        isOrganized: Boolean(options?.asset?.isOrganized),
        folderId: options?.asset?.folderId,
        fileType
      }
    }

    return NewAssetService.createAsset({
      s3Key: file.s3Key,
      name: file.name,
      size: file.size,
      contentType: fileType,
      isOrganized: Boolean(options?.asset?.isOrganized),
      folderId: options?.asset?.folderId
    }, { generateThumbnailSync: Boolean(options.generateThumbnailSync) })
  }

  /**
   * @description Uploads multiple files to S3
   * @param {File[]} files
   * @param {Object} options
   * @param {Boolean} options.generateThumbnailSync If true, we will generate thumbnails synchronously in the API. Otherwise, we will generate it in Lambda.
   * @param {Boolean} options.skipAssetCreation If true, we will not create the file in the database. We will only upload it to S3 and return the metadata
   * @param {String} options.existentS3Key This is used to upload a file and overwrite an specific file in S3 (Used for assets versioning)
   * @param {Function} options.onUploadProgress This is a function that will be called everytime the upload progress changes
   * @param {Object} options.asset Send asset's metadata to database
   * @param {Boolean} options.asset.isOrganized Control whether the file will be created as Organized or not
   * @param {Number} options.asset.folderId Set the initial folder of the asset on creation
   * @returns {Promise<Asset[]>} Returns Assets with AssetFiles Object
   */
  function uploadMultipleFileToS3 (files, options = {}) {
    if (!files?.length) {
      return Promise.resolve([])
    }

    const uploadPromises = files.map(file => uploadToS3(file, options))

    return Promise.allSettled(uploadPromises)
      .then((promisesResult) => {
        if (!promisesResult.length) {
          return []
        }

        const successfulPromises = promisesResult.filter((promise) => promise.status === 'fulfilled')
        return successfulPromises.map((uploadedFile) => uploadedFile.value)
      })
  }

  function upload (url, oData, flags, options) {
    options = options || {}
    var progressBar = $('<div class="ui indicating progress"><div class="bar"><div class="progress"></div></div></div>')
    progressBar.css('margin', '0')
    progressBar.css('width', '60%')
    $('#progress-bar-container').append(progressBar)
    flags.uploading = true

    return Upload.upload({
      url: url,
      method: options.method || 'POST',
      data: oData
    }).then(
      // success
      function (e) {
        flags.uploading = false
        progressBar.remove()

        if (options && options.cbSuccess && typeof options.cbSuccess === 'function') {
          options.cbSuccess(e.data)
        }
      },
      // error
      function (e) {
        flags.uploading = false
        flags.error = true
        flags.data = e.data
        progressBar.remove()

        if (options && options.cbError && typeof options.cbError === 'function') {
          options.cbError(e)
        }
      },
      // while upload occurs
      function (e) {
        var percent = parseInt(100 * e.loaded / e.total)
        progressBar.progress({
          percent: percent
        })
      }
    )
  }

  /**
   *
   * @param {string} url
   * @param {Object} data
   * @param {Function} progress
   * @param {Function} success
   * @param {Function} error
   */
  function basicUpload (url, data, progress, success, error) {
    return Upload.upload({
      url,
      data
    }).then(success, error, e => {
      const percent = parseInt(100 * e.loaded / e.total)
      progress(percent)
    })
  }

  /**
   * @returns {Object}
   * @param {string} sUrl
   * @param {Object[]} aFiles
   * @param {Object} aFiles[n]
   * @param {Map} oCallbacks
   * @param {Function} oCallbacks.fnSuccess
   * @param {Function} oCallbacks.fnError
   * @param {Function} oCallbacks.fnProgress
   * @param {Object?} oOptions
   * @param {number?} oOptions.simultaneousLimit
   * @param {Object[]?} oOptions.allFiles
   * @param {Map?} mExtraData
   */
  function uploadWithCustomProgress (sUrl, aFiles, mCallbacks, oOptions, mExtraData, width, height) {
    var oReqs = aFiles.map(function (file) {
      var data = angular.extend({}, mExtraData, { files: [file] })
      return {
        file: file,
        req: _upload(sUrl, data, mCallbacks.fnSuccess, mCallbacks.fnError, mCallbacks.fnProgress(file), width, height)
      }
    })
    _queueUploads(oOptions.simultaneousLimit, oOptions.allFiles, oReqs)
    return oReqs
  }

  function _upload (sUrl, oData, fnSuccess, fnError, fnProgress) {
    let upload
    return function () {
      if (!upload) {
        upload = Upload.upload({
          url: sUrl,
          data: oData
        })
        upload.then(fnSuccess, fnError, fnProgress)
      }
      return upload
    }
  }

  function _queueUploads (simultaneousLimit, allFiles, newUploads) {
    var queue = new Iterator(newUploads)

    for (var i = 0; i < simultaneousLimit; i++) {
      push()
    }

    function treatNext () {
      var next = queue.next(); var item = next.value; var done = next.done; var req

      if (!item || !item.file) {
        return null
      }

      req = item.req()

      if (!done) {
        req.then(push)
      }

      item.file.req = req
      item.file.src = {}
      return item.file
    }

    function push () {
      var newFile = treatNext()
      if (newFile && allFiles) {
        allFiles.push(newFile)
      }
    }
  }
}

function Iterator (array) {
  this.array = array
  this.lastVisitedIndex = -1
}
Iterator.prototype.next = function () {
  return {
    value: this.array[++this.lastVisitedIndex] || null,
    done: this.lastVisitedIndex === this.array.length - 1
  }
}

export { uploadService }
